import { useState, useEffect } from 'react';
import { MainContents } from '@/components/MainContents';
import { useParams, useNavigate } from '@tanstack/react-router';
import useFetchCsvMappingTemplate from '../api/useFetchCsvMappingTemplate';
import { Button } from '@/components/Button';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import Input from '@/components/Input';
import { CandidateImportMappingRow } from '../components/CandidateImportMappingRow';
import { useQuery } from '@tanstack/react-query';
import type { SharedCustomField } from '@/types/serializers';

export function CandidateImportRequestsPresetEditTemplate() {
  const { presetId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data: template, isLoading, error } = useFetchCsvMappingTemplate(presetId || '');

  // カスタムフィールド一覧を取得
  const { data: customFieldsData } = useQuery({
    queryKey: ['customFields'],
    queryFn: async () => {
      const response = await fetch('/agent/api/custom_fields');
      if (!response.ok) {
        throw new Error('カスタムフィールドの取得に失敗しました');
      }
      return response.json() as Promise<{ records: SharedCustomField[] }>;
    },
  });

  const [title, setTitle] = useState('');
  const [fieldMappings, setFieldMappings] = useState<Record<number, number[]>>({});
  const [defaultMappings, setDefaultMappings] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // パンくずリスト設定
  useSetBreadcrumbTitle(template?.title);

  // テンプレートデータを初期値に設定
  useEffect(() => {
    if (template) {
      setTitle(template.title);

      // 既存のマッピング情報を設定
      const mappings: Record<number, number[]> = {};
      template.items.forEach((item) => {
        if (!mappings[item.custom_field_id]) {
          mappings[item.custom_field_id] = [];
        }
        mappings[item.custom_field_id].push(item.csv_index);
      });
      setFieldMappings(mappings);
    }
  }, [template]);

  const handleFieldMappingChange = (customFieldId: number, csvIndices: number[]) => {
    setFieldMappings((prev) => ({
      ...prev,
      [customFieldId]: csvIndices,
    }));
  };

  const handleDefaultMappingChange = (customFieldId: number, value: string) => {
    setDefaultMappings((prev) => ({
      ...prev,
      [customFieldId]: value,
    }));
  };

  const handleSave = async () => {
    if (!template || !presetId) return;

    try {
      setIsSaving(true);

      // マッピングデータを整形
      const field_mapping = Object.entries(fieldMappings).map(([customFieldId, csvIndices]) => ({
        custom_field_id: Number(customFieldId),
        csv_index: csvIndices,
      }));

      const default_mapping = Object.entries(defaultMappings)
        .filter(([, value]) => value)
        .map(([customFieldId, value]) => ({
          custom_field_id: Number(customFieldId),
          default_value: value,
        }));

      const response = await fetch(`/agent/api/csv_mapping_templates/${presetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({
          title,
          field_mapping,
          name_mapping: template.name_mapping,
          default_mapping,
        }),
      });

      if (!response.ok) {
        throw new Error('保存に失敗しました');
      }

      alert('プリセットを保存しました');
      navigate({
        to: '/agent/candidates/candidate_import_requests/new',
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : '保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <MainContents>
        <div className="flex items-center justify-center py-x3l">
          <div className="text-body-md text-sub">読み込み中...</div>
        </div>
      </MainContents>
    );
  }

  // エラーの表示
  if (error || !template) {
    return (
      <MainContents>
        <div className="rounded-sm bg-white p-lg shadow-layer1">
          <div className="space-y-md">
            <p className="text-error text-body-md">プリセットの読み込みに失敗しました。</p>
            <Button
              color="secondary"
              size="md"
              onClick={() =>
                navigate({
                  to: '/agent/candidates/candidate_import_requests/new',
                })
              }
            >
              戻る
            </Button>
          </div>
        </div>
      </MainContents>
    );
  }

  const customFields = customFieldsData?.records || [];

  return (
    <>
      <MainContents>
        <div className="mb-20 space-y-lg">
          {/* メインコンテンツ */}
          <div className="rounded-sm bg-white p-lg shadow-layer1">
            <div className="space-y-lg">
              {/* ヘッダー */}
              <div className="border-b border-border">
                <h2 className="text-heading-md">取り込みプリセット</h2>
              </div>

              {/* 設定タイトル */}
              <div className="space-y-2">
                <label className="text-heading-xxs">設定タイトル</label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                  placeholder="テキスト"
                  className="w-[236px]"
                  required
                />
              </div>

              {/* データの割り当て */}
              <div className="space-y-2">
                <h3 className="text-heading-xxs">データの割り当て</h3>

                <div className="flex flex-col">
                  {/* ヘッダー行 */}
                  <div className="flex h-[28px] items-end gap-md bg-gray px-md">
                    <div className="w-[132px]">
                      <p className="text-heading-3xs text-sub">エージェントハブ</p>
                    </div>
                    <div className="w-[160px] opacity-0">
                      <p className="text-heading-xxs text-sub">割り当て先</p>
                    </div>
                    <div className="invisible w-6" />
                    <div className="flex-1">
                      <p className="text-heading-3xs text-sub">CSV</p>
                    </div>
                  </div>

                  {/* 情報タイプヘッダー */}
                  <div className="flex items-center gap-md border-b border-border bg-gray p-md">
                    <div className="w-[132px]">
                      <p className="text-heading-xxs text-sub">情報タイプ</p>
                    </div>
                    <div className="w-[160px]">
                      <p className="text-heading-xxs text-sub">割り当て先</p>
                    </div>
                    <div className="invisible w-6" />
                    <div className="flex-1">
                      <p className="text-heading-xxs text-sub">CSV項目/上書きする</p>
                    </div>
                  </div>

                  {/* マッピング行 */}
                  {customFields.map((field) => (
                    <CandidateImportMappingRow
                      key={field.id}
                      customField={field}
                      csvHeaders={template.header_array}
                      selectedCsvIndices={fieldMappings[field.id] || []}
                      defaultValue={defaultMappings[field.id] || ''}
                      onCsvIndicesChange={(indices: number[]) =>
                        handleFieldMappingChange(field.id, indices)
                      }
                      onDefaultValueChange={(value: string) =>
                        handleDefaultMappingChange(field.id, value)
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainContents>

      {/* 下部固定アクションバー */}
      <div className="fixed inset-x-0 bottom-0 z-10 bg-white shadow-layer3">
        <div className="mx-auto flex items-center justify-end gap-2 px-6 py-3.5">
          <Button
            color="primary"
            size="lg"
            type="button"
            onClick={handleSave}
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? '保存中...' : '変更を保存'}
          </Button>
        </div>
      </div>
    </>
  );
}
